<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions">
              <TabHeaderActions />
            </div>
            <div class="list-view-heading__controls-end">
              <b-button variant="link" class="list-view-icon-btn" :class="{ 'list-view-icon-btn--active': filtersOpen }"
                        v-b-tooltip.hover.bottom :title="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        :aria-label="filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'"
                        @click="filtersOpen = !filtersOpen">
                <AppIcon name="filter" :fill="filtersOpen" />
              </b-button>
            </div>
          </div>
        </section>

        <b-collapse v-model="filtersOpen">
          <div class="list-view-toolbar__filters">
            <div class="list-view-filter" style="flex: 1 1 220px; max-width: 300px;">
              <label class="filter-label">Tipo</label>
              <v-select :options="arr.filters.types" v-model="filters.types" placeholder="Todos" :multiple="true" :select-on-tab="true"></v-select>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Desde</label>
              <b-form-input type="date" size="sm" v-model="filters.date_start"></b-form-input>
            </div>
            <div class="list-view-filter" style="width: 150px;">
              <label class="filter-label">Hasta</label>
              <b-form-input type="date" size="sm" v-model="filters.date_end"></b-form-input>
            </div>
            <div class="list-view-filter" style="flex: 1 1 170px; max-width: 210px;">
              <label class="filter-label">Estado</label>
              <v-select :options="arr.filters.readStatus" v-model="filters.readStatus" :clearable="false" :multiple="false" :select-on-tab="true"></v-select>
            </div>
            <b-button variant="outline-dark" size="sm" @click="filterReports()" v-b-tooltip.hover title="Aplicar filtros" style="height: 31px; width: 31px; padding: 0; flex-shrink: 0;">
              <AppIcon name="filter" />
            </b-button>
          </div>
        </b-collapse>
      </b-card>

      <b-card>
        <div class="crud-sellers-reports-seller" v-if="sellers.staff">
          <b-avatar :src="sellers.staff.image" size="25px" v-if="sellers.staff.image"></b-avatar>
          <b-avatar v-else
                    size="25px"
                    :text="avatarFromName(sellers.staff.name).text"
                    :style="avatarFromName(sellers.staff.name).style"></b-avatar>
          <b class="ml-2 mr-3">{{sellers.staff.name}}</b>

          <span class="mr-3" v-if="!sellers.email">
            <AppIcon name="mail" class="mr-1" />
            <b-badge variant="warning">Sin email asociado</b-badge>
          </span>
          <span class="text-muted small mr-3" v-else>
            <AppIcon name="mail" class="mr-1" />
            <b-link :href="'mailto:' + sellers.email">{{sellers.email}}</b-link>
          </span>

          <span class="text-muted small" v-if="sellers.phone">
            <AppIcon name="call" class="mr-1" />
            <b-link :href="'tel:' + sellers.phone">{{sellers.phone}}</b-link>
          </span>
        </div>

        <b-table class="mb-0 app-table crud-sellers-reports-table"
                responsive
                :stacked="isMobileViewport ? 'md' : false"
                sticky-header="60vh"
                :hover="true"
                :small="true"
                :fixed="false"
                :items="table.items"
                :fields="table.fields"
                @row-clicked="onRowClicked"
                :busy="table.isBusy"
                v-if="table.items.length || table.isBusy">

            <template v-slot:table-busy>
              <div class="text-center text-danger my-2">
                <b-spinner class="align-middle"></b-spinner>
                <strong>Cargando...</strong>
              </div>
            </template>

            <template v-slot:cell(date)="data">
              {{moment(data.item.date).format('DD/MM/YYYY')}}
            </template>

            <template v-slot:cell(type)="data">
              <b-badge :variant="reportTypeVariant(data.item.type)" pill>
                <AppIcon :name="reportTypeIcon(data.item.type)" class="mr-1" />{{reportTypeLabel(data.item.type)}}
              </b-badge>
            </template>

            <template v-slot:cell(customers_id)="data">
              <div v-if="data.item.responsable">
                <b-avatar :src="data.item.responsable.image" size="25px" v-if="data.item.responsable.image"></b-avatar>
                <b-avatar v-else
                          size="25px"
                          :text="avatarFromName(data.item.responsable.name).text"
                          :style="avatarFromName(data.item.responsable.name).style"></b-avatar>
                <span class="ml-1">{{data.item.responsable.name}}</span>
                <AppIcon name="circle-fill" fill v-if="!data.item.responsable.active" class="ml-2 text-danger" v-b-tooltip.hover title="Cliente INACTIVO" />
              </div>
              <span v-else class="text-muted">—</span>
            </template>

            <template v-slot:cell(observations)="data">
              <div v-if="data.item.observations" class="text-truncate crud-sellers-reports-observations" v-b-tooltip.hover :title="data.item.observations">
                {{data.item.observations}}
              </div>
              <span v-else class="text-muted">—</span>
            </template>

            <template v-slot:cell(amount)="data">
              <span v-if="data.item.type == 'COBRANZAS' && data.item.amount">
                {{Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(data.item.amount)}}
              </span>
            </template>

            <template v-slot:cell(read)="data">
              <b-badge :variant="data.item.read ? 'secondary' : 'dark'" pill>{{data.item.read ? 'Leído' : 'Sin abrir'}}</b-badge>
            </template>

            <template v-slot:cell(f_action)="data">
              <b-dropdown right
                          no-caret
                          size="sm"
                          variant="link"
                          boundary="window"
                          :toggle-class="isMobileViewport ? 'crud-sellers-reports-action-toggle-labeled' : 'app-table-action-toggle'"
                          :class="{ 'pull-right': !isMobileViewport }">
                <template #button-content>
                  <AppIcon name="more_vert" :label="isMobileViewport ? 'Opciones' : 'Acciones'" :class="{ 'mr-1': isMobileViewport }" />
                  <span v-if="isMobileViewport">Opciones</span>
                </template>
                <b-dropdown-item @click="goDetailReport(data.item, data.item.type)">
                  <AppIcon name="visibility" /> Ver detalle
                </b-dropdown-item>
              </b-dropdown>
            </template>
        </b-table>
        <EmptyState v-else icon="reports" title="No se encontraron informes" description="Ajustá los filtros para ampliar la búsqueda." />

        <div class="app-table-footer">
          <span class="list-view-toolbar__count" v-if="table.tablePaginate">{{ table.tablePaginate.total }} registros</span>
        </div>
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
            <AppIcon name="back" />
            Volver
          </b-button>

          <nav v-if="table.tablePaginate">
            <b-pagination class="mb-0"
                          size="sm"
                          pills
                          :total-rows="table.tablePaginate.total"
                          :per-page="table.tablePaginate.per_page"
                          v-model="table.currentPage"
                          @input="loadReports()" />
          </nav>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>
<script>
  import serviceAPI from './services'
  import Profiles from '@/config/profiles'
  import Error from '@/handler/error'
  import Session from '@/handler/session'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import Funciones from '@/handler/funciones'
  import StorageLocal from '@/handler/storageLocal'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'

  export default {
    components: {
      TabHeaderActions,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.VENDEDORES,
          profile_id: Profiles.PERSONAL,
          view_reference: 'crudSellersReports',
          elements: {}
        },
        sellersID: 0,
        sellers: {},
        filtersOpen: false,
        isMobileViewport: false,
        table: {
          items: [],
          fields: [],
          currentPage: 1,
          isBusy: false,
          tablePaginate: null,
        },
        filters: {
          types: [],
          date_start: '',
          date_end: '',
          readStatus: {code: 'all', label: 'Todos'},
        },
        arr: {
          filters: {
            types: [],
            readStatus: [
              {code: 'all', label: 'Todos'},
              {code: true, label: 'Leídos'},
              {code: false, label: 'Sin abrir'},
            ],
          },
        },
        reports: {
          access: {
            visits: false,
            payments: false,
            news: false,
            general: false,
          },
        },
        modulesActive: [],
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    mounted () {
      this.sellersID = this.$route.params.sellersID
      this.modulesActive = Session.getSession().auth.user.permissions.modules

      this.configAccess()
      this.loadFieldTable()
      this.load()

      this.filtersOpen = StorageLocal.getValue('filters_open_crud_sellers_reports') == 'true'

      this.mobileMql = window.matchMedia('(max-width: 767.98px)')
      this.isMobileViewport = this.mobileMql.matches
      this.mobileMql.addEventListener('change', this.updateMobileViewport)
    },
    beforeDestroy() {
      if(this.mobileMql) {
        this.mobileMql.removeEventListener('change', this.updateMobileViewport)
      }
    },
    watch: {
      filtersOpen(value) {
        StorageLocal.setValue('filters_open_crud_sellers_reports', value ? 'true' : 'false')
      },
      isMobileViewport() {
        this.loadFieldTable()
      },
    },
    methods: {
      // RESPONSIVE
      updateMobileViewport(e) {
        this.isMobileViewport = e.matches
      },
      alignClass(desktopClass) {
        return this.isMobileViewport ? 'text-left' : desktopClass
      },
      avatarFromName(name) {
        return Funciones.avatarFromName(name)
      },
      loadFieldTable() {
        this.table.fields = []
        this.table.fields.push({key: 'date', label: 'Fecha', class: 'align-middle ' + this.alignClass('text-left'), width: '110px'})
        this.table.fields.push({key: 'type', label: 'Tipo', class: 'align-middle ' + this.alignClass('text-left'), width: '140px'})
        this.table.fields.push({key: 'customers_id', label: 'Cliente', class: 'align-middle ' + this.alignClass('text-left')})
        this.table.fields.push({key: 'observations', label: 'Detalle', class: 'align-middle text-wrap ' + this.alignClass('text-left')})
        this.table.fields.push({key: 'amount', label: 'Monto', class: 'align-middle ' + this.alignClass('text-right'), width: '120px'})
        this.table.fields.push({key: 'read', label: 'Estado', class: 'align-middle ' + this.alignClass('text-center'), width: '110px'})
        this.table.fields.push({key: 'f_action', label: this.isMobileViewport ? 'Acción' : '', class: 'align-middle ' + this.alignClass('text-right'), width: '50px'})
      },
      // Íconos y colores por tipo -- mismo criterio semántico que ya usa el
      // resto del sistema para distinguir orígenes de datos heterogéneos
      // (ej. crud-hours-assignment: proyecto vs actividad).
      reportTypeIcon(type) {
        var icons = {VISITAS: 'assignment', COBRANZAS: 'money', NOVEDADES: 'megaphone', GENERAL: 'reports'}
        return icons[type] || 'description'
      },
      reportTypeVariant(type) {
        var variants = {VISITAS: 'info', COBRANZAS: 'success', NOVEDADES: 'warning', GENERAL: 'secondary'}
        return variants[type] || 'secondary'
      },
      reportTypeLabel(type) {
        var labels = {VISITAS: 'Visita', COBRANZAS: 'Cobranza', NOVEDADES: 'Novedad', GENERAL: 'General'}
        return labels[type] || type
      },
      onRowClicked(item, index, event) {
        if(!item) return

        if(event && event.target) {
          const ignoreTags = ['INPUT', 'BUTTON', 'A', 'SELECT', 'TEXTAREA', 'LABEL', 'SVG', 'PATH']
          if(ignoreTags.includes(event.target.tagName)) return
        }

        this.goDetailReport(item, item.type)
      },
      load() {
        this.loadSeller()
        this.loadReports()
      },
      loadSeller() {
        let loader = this.$loading.show();

        var result = serviceAPI.mostrarVendedor(this.sellersID)

        result.then((response) => {
          this.sellers = response.data
          loader.hide()
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        });
      },
      filterReports() {
        this.table.currentPage = 1
        this.loadReports()
      },
      loadReports() {
        this.table.isBusy = true

        var data = {
          seller_id: this.sellersID,
          types: this.filters.types.map(value => value.code),
          date_start: this.filters.date_start,
          date_end: this.filters.date_end,
        }
        if(this.filters.readStatus && this.filters.readStatus.code !== 'all') {
          data.readStatus = this.filters.readStatus.code
        }

        var result = serviceAPI.obtenerReportsBySellers(data, this.table.currentPage)

        result.then((response) => {
          var data = response.data
          this.table.tablePaginate = data
          this.table.items = data.data
          this.table.isBusy = false
        })
        .catch(error => {
          this.table.isBusy = false
          this.$awn.alert(Error.showError(error))
        });
      },
      goDetailReport(item, type) {
        if(type=='VISITAS') {
          this.$router.push({ name: 'ReportsVisitsStaffDetailReport', params: {reportID: item.id} })
        }

        if(type=='COBRANZAS') {
          this.$router.push({ name: 'ReportsPaymentStaffDetailReport', params: {reportID: item.id} })
        }

        if(type=='NOVEDADES') {
          this.$router.push({ name: 'ReportsNewsStaffDetailReport', params: {reportID: item.id} })
        }

        if(type=='GENERAL') {
          this.$router.push({ name: 'ReportsGeneralStaffDetailReport', params: {reportID: item.id} })
        }
      },
      configAccess() {
        this.modulesActive.forEach(element => {

          if(element.id == Modules.INFORME_VISITAS ||
             element.id == Modules.INFORME_COBRANZAS ||
             element.id == Modules.INFORME_NOVEDADES ||
             element.id == Modules.INFORME_GENERAL) {

              if(element.id == Modules.INFORME_VISITAS) {
                this.reports.access.visits = true
              }
              if(element.id == Modules.INFORME_COBRANZAS) {
                this.reports.access.payments = true
              }
              if(element.id == Modules.INFORME_NOVEDADES) {
                this.reports.access.news = true
              }
              if(element.id == Modules.INFORME_GENERAL) {
                this.reports.access.general = true
              }
          }

        })

        this.arr.filters.types = []
        if(this.reports.access.visits) this.arr.filters.types.push({code: 'VISITAS', label: 'Visitas'})
        if(this.reports.access.payments) this.arr.filters.types.push({code: 'COBRANZAS', label: 'Cobranzas'})
        if(this.reports.access.news) this.arr.filters.types.push({code: 'NOVEDADES', label: 'Novedades'})
        if(this.reports.access.general) this.arr.filters.types.push({code: 'GENERAL', label: 'General'})
      },
    }
  }
</script>
<style>
  .crud-sellers-reports-observations {
    max-width: 320px;
  }
  .crud-sellers-reports-seller {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding-bottom: 12px;
    margin-bottom: 12px;
    border-bottom: 1px solid var(--app-color-border);
  }
  /* Excepción al kebab mudo de .app-table: esta tabla no tiene drawer que
     aloje las acciones, así que se conserva también en mobile — como link
     de texto alineado a la izquierda, no como botón con caja. */
  .btn.crud-sellers-reports-action-toggle-labeled {
    display: inline-flex !important;
    align-items: center;
    justify-content: flex-start;
    width: auto !important;
    height: auto !important;
    min-height: 0;
    padding: 0 !important;
    border: none !important;
    background: transparent !important;
    box-shadow: none !important;
    color: var(--app-color-text-secondary) !important;
    font-size: 13px;
    line-height: 1.15;
  }
  .btn.crud-sellers-reports-action-toggle-labeled:hover,
  .btn.crud-sellers-reports-action-toggle-labeled:focus {
    color: var(--app-color-primary) !important;
    text-decoration: underline;
  }
  /* Pisa la regla global que oculta el label del último field y lo alinea
     a la derecha (pensada para el kebab ícono-only sin texto). */
  @media (max-width: 767.98px) {
    .crud-sellers-reports-table.app-table.b-table-stacked-md > tbody > tr > td:last-child::before {
      content: attr(data-label) !important;
    }
    .crud-sellers-reports-table.app-table.b-table-stacked-md > tbody > tr > td:last-child {
      text-align: left !important;
    }
  }
</style>
